'use client';

import { useLocale, useMessages, type ShellMessages } from '../../portal';
import { LiveSearchBox } from './LiveSearchBox';

// =============================================================================
// ModuleLiveSearchBox — module-scoped wrapper over LiveSearchBox
// =============================================================================
//
// A thin composition over <LiveSearchBox> that pre-fills the
// scope-related props for a content module. Used by per-module
// layouts when they want to inject a search box into the shell
// header (replacing the global tenant search for users browsing
// inside that module).
//
// One component for every module — video, blog, wiki, courses,
// articles — keyed by `kind`. The behaviour identical to the global
// LiveSearchBox EXCEPT:
//
//   - Suggest dropdown is filtered to the module's kind (backend
//     receives `?kind=video` and ranks/returns only video hits).
//   - localStorage recents are namespaced by kind so the user's
//     video search history doesn't pollute their blog search
//     history (and vice versa).
//   - Enter / "see all results" navigates to the module-scoped
//     /search route (e.g. /{tenant}/video/search?q=X) rather than
//     the tenant-global /search. This keeps the user inside the
//     module's shell across the entire suggest → results → click-
//     through journey, avoiding the cross-shell flicker that bit
//     the previous architecture.
//
// Why a separate component instead of forcing every caller to pass
// the three props by hand:
//
//   - Encodes the "module-scoped search" intent in one place so the
//     scope/href/recents triple stays in sync (a caller passing
//     kind='video' but forgetting recentsScope or resultsHref would
//     produce subtle bugs — mixed recents, wrong shell after Enter).
//   - Makes the call sites at module layouts trivial:
//       `<ModuleLiveSearchBox kind="video" tenantSlug={t} />`
//     no manual URL or storage-key wrangling per module.
//   - Future scope-related additions (e.g. per-module trending,
//     kind-specific result-row treatment) land here without
//     touching the underlying LiveSearchBox or its many callers.
//
// Visual register
// ---------------
// Same as LiveSearchBox — defaults to the inline pill mode. The
// header-style-specific visual hints (inverse on dark headers,
// flush on split headers) aren't threaded through automatically;
// modules that ship into cinematic-immersive headers would render
// a light pill against dark chrome. If that becomes a real concern
// the slot mechanism in moduleChrome can be upgraded to a render-
// prop pattern with variant hints. Not solving it speculatively.

/** Common content-module kinds. Open string union — the runtime's
 *  LiveSearchBox treats `kind` as an opaque tag forwarded to the
 *  host's SearchSource adapter, so hosts can extend with module-
 *  specific tags without changing this type. */
export type SearchKind =
  | 'video'
  | 'series'
  | 'course'
  | 'article'
  | 'blog'
  | 'wiki'
  | 'channel'
  | 'live'
  | (string & {});

interface Props {
  /** Tenant slug — required for API + URL construction. */
  tenantSlug: string;
  /** Module kind. Drives suggest filter, recents namespacing, and
   *  is the conventional default for the results-page path segment
   *  ({tenant}/{kind}/search). */
  kind: SearchKind;
  /** Base URL the dropdown's "see all results" action navigates to.
   *  Defaults to `/{tenantSlug}/{kind}/search` — the conventional
   *  per-module search route. Override only when the module's search
   *  page lives at a different path. */
  resultsHref?: string;
  /** Placeholder text. Defaults to a localized "Search {kind}…" resolved
   *  against the active locale. Pass a translated string to override. */
  placeholder?: string;
  /** Visual override — render with inverse styling for dark headers. */
  inverse?: boolean;
  /** Fill the container's width instead of sizing to content. */
  fillWidth?: boolean;
  /** Render flush (no border, just bottom rule) for split headers. */
  flush?: boolean;
}

export function ModuleLiveSearchBox({
  tenantSlug,
  kind,
  resultsHref,
  placeholder,
  inverse,
  fillWidth,
  flush,
}: Props) {
  // Read the active locale from the render tree so the module search box
  // localizes both its placeholder and the static suggest-dropdown strings
  // (recent searches / trending / empty state) inside LiveSearchBox.
  const { locale, fallback } = useLocale();
  // Host-injected common.json strings (see shellMessages.ts); null when the
  // shell is mounted standalone, in which case the built-in map below wins.
  const messages = useMessages();
  const defaultResultsHref = `/${encodeURIComponent(tenantSlug)}/${kind}/search`;
  const defaultPlaceholder = modulePlaceholder(kind, locale, fallback, messages);

  return (
    <LiveSearchBox
      tenantSlug={tenantSlug}
      variant="inline"
      kind={kind}
      resultsHref={resultsHref ?? defaultResultsHref}
      recentsScope={kind}
      placeholder={placeholder ?? defaultPlaceholder}
      inverse={inverse}
      fillWidth={fillWidth}
      flush={flush}
      locale={locale}
      localeFallback={fallback}
    />
  );
}

// Built-in per-kind placeholder fallbacks, used only when the host hasn't
// injected `search.module.<kind>` (i.e. the shell is mounted standalone).
// Real copy lives in the app's common.json — see shellMessages.ts. A plain
// `Search ${kind}s…` produced odd output for non-pluralizable kinds
// ("blogs", "wikis", "seriess"), so each kind is spelled out. Operators
// wanting different copy pass `placeholder` directly, which always wins.
const DEFAULT_PLACEHOLDERS: Record<string, Record<string, string>> = {
  video: { tr: 'Video ara…', en: 'Search videos…' },
  series: { tr: 'Seri ara…', en: 'Search series…' },
  course: { tr: 'Eğitim ara…', en: 'Search courses…' },
  article: { tr: 'Makale ara…', en: 'Search articles…' },
  blog: { tr: 'Blogda ara…', en: 'Search the blog…' },
  wiki: { tr: 'Wiki ara…', en: 'Search the wiki…' },
  channel: { tr: 'Kanal ara…', en: 'Search channels…' },
  live: { tr: 'Canlı yayın ara…', en: 'Search live events…' },
  micro: { tr: 'Mikro öğrenmede ara…', en: 'Search micro-learning…' },
};

/** Resolve the per-kind placeholder: host-injected {@code common.json}
 *  string → built-in fallback (locale → fallback locale → English) →
 *  a generic "Search…". */
function modulePlaceholder(
  kind: string,
  locale: string,
  fallback: string,
  messages: ShellMessages | null,
): string {
  const hosted = messages?.[`search.module.${kind}`];
  if (hosted) return hosted;
  const byLocale = DEFAULT_PLACEHOLDERS[kind];
  if (byLocale)
    return byLocale[locale] ?? byLocale[fallback] ?? byLocale['en'] ?? `Search ${kind}…`;
  return locale === 'tr' ? 'Ara…' : `Search ${kind}…`;
}
